import { useState, useEffect, useMemo } from 'preact/hooks';
import { getConfig } from '../config';
import { getCart, getPaymentMethods, setPaymentMethod, refreshAuthAndCart, getSettings, getOldQnr, placePreOrder } from '../api';
import { store } from '../store';
import { leaveCheckoutHostPage, navigateToView } from '../navigation';
import { t } from '../i18n';
import { SearchableSelect } from './SearchableSelect';
import { translateCardType, translatePaymentName } from '../utils/paymentLabels';
import { emit } from '../analytics';

const IcoArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
  </svg>
);
const IcoAlert = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4m.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2"/>
  </svg>
);
const IcoCash = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3 4.5a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5M11.5 4a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1zm0 2a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1zm0 2a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1zm0 2a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1zm0 2a.5.5 0 0 0 0 1h1a.5.5 0 0 0 0-1" />
    <path d="M2.354.646a.5.5 0 0 0-.801.13l-.5 1A.5.5 0 0 0 1 2v13H.5a.5.5 0 0 0 0 1h15a.5.5 0 0 0 0-1H15V2a.5.5 0 0 0-.053-.224l-.5-1a.5.5 0 0 0-.8-.13L13 1.293l-.646-.647a.5.5 0 0 0-.708 0L11 1.293l-.646-.647a.5.5 0 0 0-.708 0L9 1.293 8.354.646a.5.5 0 0 0-.708 0L7 1.293 6.354.646a.5.5 0 0 0-.708 0L5 1.293 4.354.646a.5.5 0 0 0-.708 0L3 1.293zm-.217 1.198.51.51a.5.5 0 0 0 .707 0L4 1.707l.646.647a.5.5 0 0 0 .708 0L6 1.707l.646.647a.5.5 0 0 0 .708 0L8 1.707l.646.647a.5.5 0 0 0 .708 0L10 1.707l.646.647a.5.5 0 0 0 .708 0L12 1.707l.646.647a.5.5 0 0 0 .708 0l.509-.51.137.274V15H2V2.118z" />
  </svg>
);
const IcoBitcoin = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M5.5 13v1.25c0 .138.112.25.25.25h1a.25.25 0 0 0 .25-.25V13h.5v1.25c0 .138.112.25.25.25h1a.25.25 0 0 0 .25-.25V13h.084c1.992 0 3.416-1.033 3.416-2.82 0-1.502-.98-2.178-1.816-2.425v-.064c.65-.19 1.538-.86 1.538-2.21C12.172 3.798 10.8 3 9.006 3H8.5V1.75a.25.25 0 0 0-.25-.25h-1a.25.25 0 0 0-.25.25V3h-.5V1.75a.25.25 0 0 0-.25-.25h-1a.25.25 0 0 0-.25.25V3H3.759a.25.25 0 0 0-.25.25V13h1.5v-1.5h.491zm1.25-4.25h2.769c1.156 0 1.781.662 1.781 1.656 0 1.018-.624 1.594-1.781 1.594H6.75zm0-4h2.256c1.006 0 1.547.519 1.547 1.406 0 .919-.541 1.469-1.547 1.469H6.75z"/>
  </svg>
);
const IcoCreditCard = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H0zm0 3v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7zm3 2h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1"/>
  </svg>
);

function getMonths(): Record<string, string> {
  return Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => {
      const n = String(i + 1).padStart(2, '0');
      return [n, t(`month.${i + 1}`)];
    })
  ) as Record<string, string>;
}

interface PaymentMethod {
  paymentid: string;
  payment_name: string;
  type: string;
  payment_desc?: string;
  payment_inst?: string;
  link_source?: string;
  gateway_redirect?: boolean;
  fields?: {
    card_type?: { options?: string };
    [k: string]: { min_width?: number; max_width?: number; options?: string } | undefined;
  };
  template?: string;
}

interface PmForm {
  card_type?: string;
  card_number?: string;
  card_expire_Month?: string;
  card_expire_Year?: string;
  card_cvv2?: string;
  CheckNumber?: string;
  BankName?: string;
  RoutingNumber?: string;
  AccountNumber?: string;
}

function getCardTypes(pm: PaymentMethod): string[] {
  const opt = pm.fields?.card_type?.options;
  if (!opt) return [];
  return opt.split('|').map((s) => s.trim()).filter(Boolean);
}

function getCurrentYear(): number {
  return new Date().getFullYear();
}

function normalizeCardType(raw: string): string {
  const t = (raw ?? '').trim().toUpperCase();
  if (t === 'AMERICAN EXPRESS' || t === 'AMEX') return 'AMEX';
  if (t === 'MASTERCARD' || t === 'MASTER' || t === 'MC') return 'MC';
  if (t === 'VISA') return 'VISA';
  return t;
}

/** Backend expects mmyy (same as legacy ms2024 checkout). */
function buildCardExpire(month: string, year: string): string {
  const m = (month ?? '').trim().padStart(2, '0');
  const y = String(year ?? '').trim();
  if (!/^\d{2}$/.test(m) || !/^\d{4}$/.test(y)) return '';
  return `${m}${y.slice(-2)}`;
}

const PAYMENT_FIELD_ERROR_KEYS: Record<string, string> = {
  card_type: 'validate.ccTypeEmpty',
  card_number: 'validate.ccEmpty',
  card_expire: 'validate.expEmpty',
  card_cvv2: 'validate.cvvEmpty',
  BankName: 'validate.bankEmpty',
  CheckNumber: 'validate.chequeEmpty',
  RoutingNumber: 'validate.routingEmpty',
  AccountNumber: 'validate.accountEmpty',
};

function paymentFieldMessage(field: string): string {
  const key = PAYMENT_FIELD_ERROR_KEYS[field];
  return key ? t(key) : t('validate.fieldInvalid');
}

function flattenErrorMessage(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value[0];
    if (typeof first === 'string') return first;
    if (Array.isArray(first)) return flattenErrorMessage(first);
    return first != null ? String(first) : '';
  }
  return value != null ? String(value) : '';
}

/** Map API / shared validation keys to checkout form fields. */
function normalizePaymentFieldErrors(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [key, val] of Object.entries(raw)) {
    const msg = flattenErrorMessage(val).trim();
    if (!msg) continue;
    if (key === 'card_expire') {
      out.card_expire_Month = out.card_expire_Month || msg;
      continue;
    }
    out[key] = msg;
  }
  return out;
}

function scrollToFirstPaymentError() {
  if (typeof document === 'undefined') return;
  requestAnimationFrame(() => {
    const root = document.querySelector('.shop-widget-checkout-page');
    if (!root) return;
    const target = (
      root.querySelector('.is-invalid')
      ?? root.querySelector('.sw-cc-field .text-danger, .mb-5 .text-danger')
    ) as HTMLElement | null;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

function paymentFieldError(errors: Record<string, string>, ...keys: string[]): string {
  for (const key of keys) {
    const msg = errors[key]?.trim();
    if (msg) return msg;
  }
  return '';
}

/** Luhn check for credit card number (digits only) */
function luhnCheck(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (!/^\d+$/.test(digits)) return false;
  let sum = 0;
  let even = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (even) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    even = !even;
  }
  return sum % 10 === 0;
}

/** Card number length by type (PHP Respect\creditCard) */
function cardNumberValid(cardType: string, number: string): { ok: boolean; msg?: string } {
  const digits = number.replace(/\D/g, '');
  if (!digits.length) return { ok: false, msg: t('validate.ccEmpty') };
  if (!luhnCheck(digits)) return { ok: false, msg: t('validate.ccInvalid') };
  const len = digits.length;
  if (cardType === 'AMEX') {
    if (len !== 15) return { ok: false, msg: t('validate.ccAmex') };
  } else if (cardType === 'MC') {
    if (len !== 16) return { ok: false, msg: t('validate.ccMaster') };
  } else {
    if (len < 13 || len > 19) return { ok: false, msg: t('validate.ccVisa') };
  }
  return { ok: true };
}

export function CheckoutPage() {
  const config = getConfig();
  const months = getMonths();
  const yearOptions = useMemo(
    () =>
      Object.fromEntries(
        Array.from({ length: 13 }, (_, i) => {
          const y = getCurrentYear() + i;
          return [String(y), String(y)];
        })
      ),
    []
  );
  const [cart, setCart] = useState<any>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [pmForm, setPmForm] = useState<PmForm>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState<any>({});

  const setPmField = (key: keyof PmForm, value: string) => {
    setPmForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      if (key === 'card_expire_Month' || key === 'card_expire_Year') {
        delete next.card_expire;
        delete next.card_expire_Month;
        delete next.card_expire_Year;
      }
      return next;
    });
    if (apiError) setApiError(null);
  };

  const applyPaymentErrors = (raw: Record<string, unknown> | null | undefined, pm: PaymentMethod) => {
    const fieldErrors = normalizePaymentFieldErrors(raw);
    const fieldKeys = Object.keys(fieldErrors).filter((k) => k !== 'paymentid');
    emit('sw:checkout-validation-error', {
      step: 'payment',
      error_count: Math.max(1, Object.keys(fieldErrors).length),
      fields: fieldKeys.length ? fieldKeys : ['paymentid'],
    });
    setErrors(fieldErrors);
    setSelectedPayment(pm.paymentid);
    if (fieldKeys.length) {
      setApiError(null);
      scrollToFirstPaymentError();
      return;
    }
    const general = fieldErrors.paymentid || flattenErrorMessage(raw?.paymentid) || t('checkout.paymentFailed');
    setApiError(general);
    store.addToast(general, 'error');
  };

  const validatePm = (pm: PaymentMethod): Record<string, string> | null => {
    const e: Record<string, string> = {};
    const cardType = normalizeCardType(pmForm.card_type ?? '');
    const cardExpire = buildCardExpire(pmForm.card_expire_Month ?? '', pmForm.card_expire_Year ?? '');
    const raw: Record<string, string> = {
      card_type: cardType,
      card_number: (pmForm.card_number ?? '').replace(/\s|-/g, ''),
      card_expire_Month: pmForm.card_expire_Month ?? '',
      card_expire_Year: pmForm.card_expire_Year ?? '',
      card_expire: cardExpire,
      card_cvv2: pmForm.card_cvv2 ?? '',
      CheckNumber: pmForm.CheckNumber ?? '',
      BankName: pmForm.BankName ?? '',
      RoutingNumber: pmForm.RoutingNumber ?? '',
      AccountNumber: pmForm.AccountNumber ?? '',
    };

    // 1) Backend: foreach payment_method["fields"] min_width/max_width
    const fields = pm.fields ?? {};
    for (const [k, v] of Object.entries(fields)) {
      const val = raw[k] ?? (pmForm as Record<string, string>)[k] ?? '';
      if (typeof v === 'object' && v !== null && 'min_width' in v && v.min_width != null) {
        if (val.length < (v.min_width as number)) {
          e[k] = paymentFieldMessage(k);
        }
      }
      if (typeof v === 'object' && v !== null && 'max_width' in v && v.max_width != null) {
        if (val.length > (v.max_width as number)) {
          e[k] = paymentFieldMessage(k);
        }
      }
    }

    if (pm.type === 'creditcard') {
      if (!raw.card_type.trim()) e.card_type = t('validate.ccTypeEmpty');
      const cardRes = cardNumberValid(raw.card_type, raw.card_number);
      if (!cardRes.ok) e.card_number = cardRes.msg!;
      if (!/^\d{4}$/.test(raw.card_expire)) {
        e.card_expire_Month = t('validate.expEmpty');
      }
      if (!/^\d{3,4}$/.test(raw.card_cvv2)) {
        e.card_cvv2 = raw.card_cvv2 ? t('validate.cvvDigits') : t('validate.cvvEmpty');
      }
    } else if (pm.type === 'echeque') {
      if (!raw.BankName.trim()) e.BankName = t('validate.bankEmpty');
      else if (raw.BankName.length > 32) e.BankName = t('validate.bankLength');
      if (!raw.CheckNumber.trim()) e.CheckNumber = t('validate.chequeEmpty');
      else if (!/^\d+$/.test(raw.CheckNumber)) e.CheckNumber = t('validate.chequeDigits');
      else if (raw.CheckNumber.length > 16) e.CheckNumber = t('validate.chequeLength');
      if (!raw.RoutingNumber.trim()) e.RoutingNumber = t('validate.routingEmpty');
      else if (!/^\d+$/.test(raw.RoutingNumber)) e.RoutingNumber = t('validate.routingDigits');
      else if (raw.RoutingNumber.length > 16) e.RoutingNumber = t('validate.routingLength');
      if (!raw.AccountNumber.trim()) e.AccountNumber = t('validate.accountEmpty');
      else if (!/^\d+$/.test(raw.AccountNumber)) e.AccountNumber = t('validate.accountDigits');
      else if (raw.AccountNumber.length > 16) e.AccountNumber = t('validate.accountLength');
    }

    if (Object.keys(e).length) return e;
    return null;
  };

  const buildPmPayload = (pm: PaymentMethod): Record<string, string> | undefined => {
    if (pm.type === 'creditcard') {
      const cardType = normalizeCardType(pmForm.card_type ?? '');
      const cardExpire = buildCardExpire(pmForm.card_expire_Month ?? '', pmForm.card_expire_Year ?? '');
      return {
        card_type: cardType,
        card_number: (pmForm.card_number ?? '').replace(/\s|-/g, ''),
        card_expire_Month: pmForm.card_expire_Month ?? '',
        card_expire_Year: pmForm.card_expire_Year ?? '',
        card_expire: cardExpire,
        card_cvv2: pmForm.card_cvv2 ?? '',
      };
    }
    if (pm.type === 'echeque') {
      return {
        CheckNumber: pmForm.CheckNumber ?? '',
        BankName: pmForm.BankName ?? '',
        RoutingNumber: pmForm.RoutingNumber ?? '',
        AccountNumber: pmForm.AccountNumber ?? '',
      };
    }
    return undefined;
  };

  const handleContinue = async (pm: PaymentMethod) => {
    emit('sw:checkout-attempt', { step: 'payment', paymentid: pm.paymentid });
    const err = validatePm(pm);
    if (err) {
      applyPaymentErrors(err, pm);
      return;
    }
    emit('sw:checkout-submit', { step: 'payment' });
    setSubmitting(true);
    setErrors({});
    setApiError(null);
    try {
      const payload = buildPmPayload(pm);
      const res = await setPaymentMethod(config, pm.paymentid, payload);

      if (res.status === 200) {
        store.setPaymentDetails({ paymentid: pm.paymentid, ...(payload || {}) });
        try {
          await refreshAuthAndCart(config, store);
        } catch (e) {
          console.error('Failed to refresh cart:', e);
        }
        const qnrVal = settings.QNR;
        const qnrEnabled = qnrVal !== 'N';
        if (qnrEnabled) {
          store.setCheckoutPaymentId(pm.paymentid);
          // После перезагрузки store пустой — подставляем QNR из сессии (getOldQnr). Иначе не затираем локальные данные.
          const hasInStore = (() => {
            const v = store.getOldQnrData();
            return v && typeof v === 'object' && Object.keys(v).length > 0;
          })();
          if (!hasInStore) {
            try {
              const qnrRes = await getOldQnr(config);
              const payload = qnrRes?.result && typeof qnrRes.result === 'object' && !Array.isArray(qnrRes.result)
                ? (qnrRes.result as Record<string, unknown>) : null;
              if (payload && Object.keys(payload).length > 0) store.setOldQnrData(payload);
            } catch (_) {}
          }
        } else {
          store.setCheckoutPaymentId(null);
        }
        store.setReturnTo(null);
        navigateToView(qnrEnabled ? 'checkout-qnr' : 'checkout-confirm');
      } else {
        applyPaymentErrors((res as { errors?: Record<string, unknown> }).errors ?? { paymentid: res.error }, pm);
      }
    } catch (err) {
      emit('sw:checkout-validation-error', { step: 'payment', error_count: 1, fields: ['paymentid'] });
      console.error('Error in handleContinue:', err);
      const errMsg = err instanceof Error ? err.message : t('checkout.error');
      setErrors({});
      setApiError(errMsg);
      store.addToast(errMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [cartRes, pmRes, settingsRes] = await Promise.all([
          getCart(config),
          getPaymentMethods(config),
          getSettings(config),
        ]);
        if (settingsRes.status === 200 && settingsRes.result) {
          setSettings(settingsRes.result);
          store.setSettings(settingsRes.result);
        }
        const cartData = cartRes.status === 200 && cartRes.result ? cartRes.result as Record<string, unknown> : null;
        if (cartData) setCart(cartData);
        if (pmRes.status === 200 && pmRes.result) {
          const methods = Object.entries(pmRes.result).map(([id, data]: [string, any]) => ({
            paymentid: id,
            ...data,
          }));
          setPaymentMethods(methods);
          setSelectedPayment(null);
          if (methods.length > 0) {
            const cartPmId = cartData?.paymentid ?? cartData?.payment_method ?? store.getCheckoutPaymentId();
            const matched = cartPmId != null
              ? methods.find((m) => String(m.paymentid) === String(cartPmId))
              : null;
            if (matched) store.setCheckoutPaymentId(null);
          }
        }
        // Pre-order on checkout load (same as static: cart_checkout triggers place-pre-order)
        const products = (cartData as any)?.products;
        if (products?.length) {
          try {
            await placePreOrder(config);
            await getCart(config).then((r) => {
              if (r.status === 200 && r.result) setCart(r.result as Record<string, unknown>);
            });
          } catch (_) {}
        }
      } catch (err) {
        console.error('Error loading checkout data:', err);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Keep a stored payment id from re-opening the accordion on checkout load.
  useEffect(() => {
    if (!cart || !paymentMethods.length) return;
    const cartPmId = (cart as Record<string, unknown>).paymentid
      ?? (cart as Record<string, unknown>).payment_method
      ?? store.getCheckoutPaymentId();
    if (cartPmId == null) return;
    const matched = paymentMethods.find((m) => String(m.paymentid) === String(cartPmId));
    if (matched) {
      store.setCheckoutPaymentId(null);
    }
  }, [cart, paymentMethods]);

  // Default CC fields when switching to a creditcard method
  useEffect(() => {
    if (!selectedPayment || !paymentMethods.length) return;
    const pm = paymentMethods.find((m) => m.paymentid === selectedPayment);
    if (pm?.type === 'creditcard') {
      const types = getCardTypes(pm);
      setPmForm((prev) => ({
        ...prev,
        ...(types.length && !prev.card_type ? { card_type: types[0] } : {}),
        ...(!prev.card_expire_Month ? { card_expire_Month: '01', card_expire_Year: String(getCurrentYear()) } : {}),
      }));
    }
  }, [selectedPayment, paymentMethods]);

  if (loading) {
    return (
      <div class="text-center py-5">
        <span class="spinner-border text-primary" role="status"></span>
        <p class="mt-2 text-muted">{t('checkout.loading')}</p>
      </div>
    );
  }

  if (!cart || !cart.products || cart.products.length === 0) {
    return (
      <div class="sw-tickets-shell">
        <div class="sw-surface-card text-center py-5 px-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 16 16" fill="currentColor" class="text-muted mb-3" style={{ opacity: .4 }} aria-hidden="true">
            <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
          </svg>
          <p class="fs-5 text-muted mb-3">{t('cartPage.empty')}</p>
          <button type="button" class="btn btn-primary" onClick={() => leaveCheckoutHostPage()}>
            {t('cartPage.startShopping')}
          </button>
        </div>
      </div>
    );
  }

  const returnToConfirm = store.getReturnTo() === 'checkout-confirm';

  return (
    <div class="shop-widget-checkout-page p-1">
      {returnToConfirm && (
        <div class="mb-3">
          <button type="button" class="btn btn-outline-secondary btn-sm" onClick={() => { store.setReturnTo(null); navigateToView('checkout-confirm'); }}>
            <IcoArrowLeft />{' '}{t('checkout.backToConfirm')}
          </button>
        </div>
      )}
      <h3 class="text-center mb-4 fw-bold" style={{ color: 'var(--sw-primary)' }}>{t('checkout.selectPayment')}</h3>

      {apiError && (
        <div class="alert alert-danger d-flex align-items-center gap-2" role="alert">
          <IcoAlert />
          <span>{apiError}</span>
        </div>
      )}

      <div class="card mb-4 border shadow-sm overflow-hidden p-4 mt-2 mb-5">
        <div class="accordion" id="accordionPayment">
        {paymentMethods.map((pm) => {
          const isSelected = selectedPayment === pm.paymentid;
          const panelId = `payment-panel-${pm.paymentid}`;
          const cardTypes = getCardTypes(pm);
          const pmFieldErrors = isSelected
            ? Object.entries(errors).filter(([k, msg]) => k !== 'paymentid' && !!msg.trim())
            : [];

          return (
            <div class="accordion-item" key={pm.paymentid}>
              <h2 class="accordion-header">
                <button
                  class={`accordion-button ${isSelected ? '' : 'collapsed'}`}
                  type="button"
                  aria-expanded={isSelected}
                  aria-controls={panelId}
                  onClick={() => {
                    if (!isSelected) emit('sw:checkout-payment-open', { step: 'payment', paymentid: pm.paymentid });
                    setSelectedPayment((current) => current === pm.paymentid ? null : pm.paymentid);
                  }}
                >
                  <span class="d-flex align-items-center gap-2">
                    {pm.type === 'echeque' && <IcoCash />}
                    {pm.paymentid === '200' && <IcoBitcoin />}
                    {pm.type !== 'echeque' && pm.paymentid !== '200' && <IcoCreditCard />}
                    {translatePaymentName(pm.payment_name, pm.type, pm.paymentid)}
                  </span>
                </button>
              </h2>
              <div id={panelId} class={`accordion-collapse collapse ${isSelected ? 'show' : ''}`}>
                {isSelected && <div class="accordion-body">
                  {pmFieldErrors.length > 0 && (
                    <div class="alert alert-danger py-2 px-3 mb-3" role="alert">
                      <ul class="mb-0 ps-3 small">
                        {pmFieldErrors.map(([field, msg]) => (
                          <li key={field}>{msg}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Credit card — payment_ccinfo.tpl */}
                  {pm.type === 'creditcard' && (
                    <div class="row">
                      <div class="col-md-6 offset-md-3 sw-cc-form">
                        {cardTypes.length > 0 && (
                          <div class="sw-cc-field">
                            <label class="form-label select-label d-block mb-1">{t('checkout.ccType')}</label>
                            <SearchableSelect
                              options={Object.fromEntries(cardTypes.map((ct) => [ct, translateCardType(ct)]))}
                              value={pmForm.card_type ?? ''}
                              onChange={(v) => setPmField('card_type', v)}
                              placeholder={t('select.placeholder')}
                              searchable={false}
                              error={!!errors.card_type}
                            />
                            {errors.card_type && <div class="small text-danger mt-2">{errors.card_type}</div>}
                          </div>
                        )}
                        <div class="sw-cc-field">
                          <label class="form-label" for={`card_number_${pm.paymentid}`}>{t('checkout.ccNumber')}</label>
                          <input
                            class={`form-control ${errors.card_number ? 'is-invalid' : ''}`}
                            type="text"
                            id={`card_number_${pm.paymentid}`}
                            maxLength={20}
                            value={pmForm.card_number ?? ''}
                            onInput={(e) => setPmField('card_number', (e.target as HTMLInputElement).value.replace(/\s|-/g, ''))}
                          />
                          {errors.card_number && <div class="small text-danger mt-2">{errors.card_number}</div>}
                        </div>
                        <div class="sw-cc-field">
                          <label class="form-label d-block mb-2">{t('checkout.expDate')}</label>
                          <div class="sw-cc-expiry-grid">
                            <div>
                              <SearchableSelect
                                options={months}
                                value={pmForm.card_expire_Month ?? ''}
                                onChange={(v) => setPmField('card_expire_Month', v)}
                                placeholder={t('select.placeholder')}
                                searchable={false}
                                error={!!paymentFieldError(errors, 'card_expire', 'card_expire_Month', 'card_expire_Year')}
                              />
                            </div>
                            <div>
                              <SearchableSelect
                                options={yearOptions}
                                value={pmForm.card_expire_Year ?? ''}
                                onChange={(v) => setPmField('card_expire_Year', v)}
                                placeholder={t('select.placeholder')}
                                searchable={false}
                                error={!!paymentFieldError(errors, 'card_expire', 'card_expire_Month', 'card_expire_Year')}
                              />
                            </div>
                          </div>
                          {paymentFieldError(errors, 'card_expire', 'card_expire_Month', 'card_expire_Year') && (
                            <div class="small text-danger mt-2">{paymentFieldError(errors, 'card_expire', 'card_expire_Month', 'card_expire_Year')}</div>
                          )}
                        </div>
                        <div class="sw-cc-field">
                          <label class="form-label" for={`card_cvv2_${pm.paymentid}`}>{t('checkout.cvv')}</label>
                          <input
                            class={`form-control ${errors.card_cvv2 ? 'is-invalid' : ''}`}
                            type="text"
                            id={`card_cvv2_${pm.paymentid}`}
                            maxLength={4}
                            value={pmForm.card_cvv2 ?? ''}
                            onInput={(e) => setPmField('card_cvv2', (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4))}
                          />
                          {errors.card_cvv2 && <div class="small text-danger mt-2">{errors.card_cvv2}</div>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* E-cheque — payment_echeque.tpl */}
                  {pm.type === 'echeque' && (
                    <div class="row">
                      <div class="col-md-6 offset-md-3">
                        {pm.payment_desc && <div class="mb-5" dangerouslySetInnerHTML={{ __html: pm.payment_desc }} />}
                        <div class="mb-5">
                          <label class="form-label" for={`CheckNumber_${pm.paymentid}`}>{t('checkout.checkNumber')}</label>
                          <input
                            class={`form-control ${errors.CheckNumber ? 'is-invalid' : ''}`}
                            type="text"
                            id={`CheckNumber_${pm.paymentid}`}
                            maxLength={16}
                            value={pmForm.CheckNumber ?? ''}
                            onInput={(e) => setPmField('CheckNumber', (e.target as HTMLInputElement).value)}
                          />
                          {errors.CheckNumber && <div class="small text-danger mt-2">{errors.CheckNumber}</div>}
                        </div>
                        <div class="mb-5">
                          <label class="form-label" for={`BankName_${pm.paymentid}`}>{t('checkout.bankName')}</label>
                          <input
                            class={`form-control ${errors.BankName ? 'is-invalid' : ''}`}
                            type="text"
                            id={`BankName_${pm.paymentid}`}
                            maxLength={32}
                            value={pmForm.BankName ?? ''}
                            onInput={(e) => setPmField('BankName', (e.target as HTMLInputElement).value)}
                          />
                          {errors.BankName && <div class="small text-danger mt-2">{errors.BankName}</div>}
                        </div>
                        <div class="mb-5">
                          <label class="form-label" for={`RoutingNumber_${pm.paymentid}`}>{t('checkout.routingNumber')}</label>
                          <input
                            class={`form-control ${errors.RoutingNumber ? 'is-invalid' : ''}`}
                            type="text"
                            id={`RoutingNumber_${pm.paymentid}`}
                            maxLength={16}
                            value={pmForm.RoutingNumber ?? ''}
                            onInput={(e) => setPmField('RoutingNumber', (e.target as HTMLInputElement).value)}
                          />
                          {errors.RoutingNumber && <div class="small text-danger mt-2">{errors.RoutingNumber}</div>}
                        </div>
                        <div class="mb-5">
                          <label class="form-label" for={`AccountNumber_${pm.paymentid}`}>{t('checkout.accountNumber')}</label>
                          <input
                            class={`form-control ${errors.AccountNumber ? 'is-invalid' : ''}`}
                            type="text"
                            id={`AccountNumber_${pm.paymentid}`}
                            maxLength={16}
                            value={pmForm.AccountNumber ?? ''}
                            onInput={(e) => setPmField('AccountNumber', (e.target as HTMLInputElement).value)}
                          />
                          {errors.AccountNumber && <div class="small text-danger mt-2">{errors.AccountNumber}</div>}
                        </div>
                        <div class="notes mb-4">
                          <p class="small text-muted">
                            <strong>{t('checkout.routingHint')}</strong><br />
                            <strong>{t('checkout.accountHint')}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bitcoin — payment_bitcoin.tpl */}
                  {pm.paymentid === '200' && (
                    <div class="row">
                      <div class="col-12">
                        <h5 class="text-success fw-semibold">{t('checkout.btcTitle')}</h5>
                        <ol>
                          <li>{t('checkout.btcStep1')}</li>
                          <li>{t('checkout.btcStep2')}</li>
                          <li>{t('checkout.btcStep3')}</li>
                        </ol>
                        <p>{t('checkout.btcOrderNote')}</p>
                        <p><a href="/bitcoin" target="_blank" rel="noopener">{t('checkout.btcHelp')}</a></p>
                      </div>
                    </div>
                  )}

                  {/* CryptoPayments — separate from the Bitcoin payment method */}
                  {pm.paymentid === '110' && pm.type !== 'creditcard' && pm.type !== 'echeque' && (
                    <div class="text-start">
                      <h5 class="text-success fw-semibold">{t('checkout.cryptoTitle')}</h5>
                      <ol>
                        <li>{t('checkout.cryptoStep1')}</li>
                        <li>{t('checkout.cryptoStep2')}</li>
                        <li>{t('checkout.cryptoStep3')}</li>
                      </ol>
                      <p><strong>{t('checkout.cryptoCoinsLabel')}</strong><br />
                        {t('checkout.cryptoCoins')}</p>
                      <p><strong>USDT &amp; USDC</strong><br />
                        {t('checkout.cryptoNetworks')}</p>
                      <p><strong>{t('checkout.cryptoBitcoinTitle')}</strong><br />
                        {t('checkout.cryptoBitcoinNote')}</p>
                    </div>
                  )}

                  {/* Other / link */}
                  {pm.type !== 'creditcard' && pm.type !== 'echeque' && pm.paymentid !== '200' && pm.paymentid !== '110' && (
                    <div class="text-center p-3">
                      {pm.payment_desc && <p>{pm.payment_desc}</p>}
                      {!(pm.template || pm.gateway_redirect || pm.link_source === 'payment_hub') && pm.payment_inst && (
                        <div dangerouslySetInnerHTML={{ __html: pm.payment_inst }} />
                      )}
                    </div>
                  )}

                  <div class="d-flex justify-content-center mt-3">
                    <button
                      type="button"
                      class="btn btn-primary btn-lg px-5"
                      onClick={() => handleContinue(pm)}
                      disabled={submitting}
                    >
                      {submitting ? t('checkout.processing') : t('checkout.continue')}
                    </button>
                  </div>
                </div>}
              </div>
            </div>
          );
        })}

        {paymentMethods.length === 0 && (
          <div class="alert alert-warning text-center">
            {t('checkout.noPayments')}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
